"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import {
  LESSON_PROGRESS_COMPLETED_EVENT,
  type LessonProgressCompletedDetail,
} from "@/features/courses/constants";

interface LessonProgressActionsProps {
  courseSlug: string;
  lessonSlug: string;
}

interface LessonProgressResponse {
  completed: boolean;
  percentWatched: number;
  percentComplete?: number;
  status?: string;
  error?: string;
  canComplete?: boolean;
  maxWatchedSeconds?: number;
}

const COMPLETION_THRESHOLD = 0.75;

export function LessonProgressActions({ courseSlug, lessonSlug }: LessonProgressActionsProps) {
  const router = useRouter();
  const [autoCompleting, setAutoCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [percentWatched, setPercentWatched] = useState(0);
  const [canComplete, setCanComplete] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastCompletionAttemptPercent, setLastCompletionAttemptPercent] = useState<number | null>(null);
  const roundedPercentWatched = Number(percentWatched.toFixed(3));
  const completionEventDispatched = useRef(false);

  useEffect(() => {
    if (completed) {
      if (!completionEventDispatched.current) {
        completionEventDispatched.current = true;
        const detail: LessonProgressCompletedDetail = {
          courseSlug,
          lessonSlug,
        };
        window.dispatchEvent(
          new CustomEvent<LessonProgressCompletedDetail>(LESSON_PROGRESS_COMPLETED_EVENT, {
            detail,
          })
        );
      }
    } else {
      completionEventDispatched.current = false;
    }
  }, [completed, courseSlug, lessonSlug]);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await fetch(`/api/learn/courses/${courseSlug}/lessons/${lessonSlug}/progress`, {
          credentials: "include",
        });

        if (response.status === 401) {
          setMessage("Please sign in to track your progress.");
          return;
        }

        if (!response.ok) {
          throw new Error("progress_fetch_failed");
        }

        const data = (await response.json()) as LessonProgressResponse;
        setCompleted(Boolean(data.completed));
        setPercentWatched(data.percentWatched || 0);
        setCanComplete((data.percentWatched || 0) >= COMPLETION_THRESHOLD);
      } catch (err) {
        console.error(err);
        setError("We could not load lesson progress.");
      }
    };

    fetchProgress().catch((err) => {
      console.error(err);
      setError("Unexpected error loading progress.");
    });
  }, [courseSlug, lessonSlug]);

  // Poll for progress updates every 5 seconds
  useEffect(() => {
    if (completed) return;

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/learn/courses/${courseSlug}/lessons/${lessonSlug}/progress`, {
          credentials: "include",
        });

        if (response.ok) {
          const data = (await response.json()) as LessonProgressResponse;
          setPercentWatched(data.percentWatched || 0);
          setCanComplete((data.percentWatched || 0) >= COMPLETION_THRESHOLD);
          if (data.completed) {
            setCompleted(true);
          }
        }
      } catch (err) {
        console.error("Failed to poll progress:", err);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [courseSlug, lessonSlug, completed]);

  useEffect(() => {
    if (!canComplete || completed) return;

    if (lastCompletionAttemptPercent !== null && roundedPercentWatched <= lastCompletionAttemptPercent) {
      return;
    }

    let isActive = true;

    const completeLesson = async () => {
      setAutoCompleting(true);
      setError(null);
      setMessage(null);
      setLastCompletionAttemptPercent(roundedPercentWatched);

      try {
        const response = await fetch(`/api/learn/courses/${courseSlug}/lessons/${lessonSlug}/progress`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ completed: true, percentWatched: roundedPercentWatched }),
          credentials: "include",
        });

        if (response.status === 401) {
          if (!isActive) return;
          setError("Please sign in to save your progress.");
          return;
        }

        if (!response.ok) {
          const payload = (await response.json().catch(() => ({}))) as LessonProgressResponse;
          if (!isActive) return;
          setError(payload.error ?? "We could not update your progress.");
          return;
        }

        const data = (await response.json()) as LessonProgressResponse;

        if (!isActive) return;

        if (data.completed) {
          setCompleted(true);
          setMessage("Lesson marked as complete! Keep going.");
          router.refresh();
        } else {
          setError(`You must watch at least ${COMPLETION_THRESHOLD * 100}% of the video to complete this lesson.`);
        }
      } catch (err) {
        console.error(err);
        if (isActive) {
          setError("Unexpected error saving progress. Please try again.");
        }
      } finally {
        if (isActive) {
          setAutoCompleting(false);
        }
      }
    };

    completeLesson().catch((err) => {
      console.error("Failed to auto-complete lesson:", err);
      if (isActive) {
        setAutoCompleting(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [canComplete, completed, courseSlug, lessonSlug, router, roundedPercentWatched, lastCompletionAttemptPercent]);

  const progressPercentage = Math.round(percentWatched * 100);
  const requiredPercentage = COMPLETION_THRESHOLD * 100;

  return (
    <div className="space-y-3 text-sm text-muted-foreground">
      <h3 className="text-sm font-semibold text-foreground md:text-base">Lesson progress</h3>

      {/* Progress bar */}
      {!completed && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span>Watch progress</span>
            <span className={canComplete ? "text-emerald-600 font-medium" : ""}>
              {progressPercentage}% / {requiredPercentage}%
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted md:h-2">
            <div
              className={`h-full transition-all duration-300 ${canComplete ? "bg-emerald-500" : "bg-primary"}`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      <p className="text-xs md:text-sm">
        {completed
          ? "✓ This lesson is complete."
          : canComplete
            ? "✓ You've watched enough to complete this lesson."
            : `Watch at least ${requiredPercentage}% of the video to complete this lesson.`}
      </p>

      {autoCompleting && !completed && !error && (
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground md:text-sm">Finishing up…</p>
      )}
      {message && <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 md:text-sm">{message}</p>}
      {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive md:text-sm">{error}</p>}
    </div>
  );
}
